import { $config } from '@client/core/atoms/config.js';
import {
  ApiResolver,
  type InitialState
} from '@schibsted-nmp/advertising-shared';

import type { Window } from './Window.js';

import type { Api } from './index.js';

export const { globalPropertyName, unsafePropertyName } =
  ApiResolver.getGlobalPropertyNames('_googCsa');

export class Resolver extends ApiResolver<
  Api.Uninitialized,
  Api.Initialized,
  Api.Loaded,
  Window
> {
  /**
   * Provides synchronous access to a fully loaded global reference.
   *
   * *Warning: throws if it's not fully loaded!*
   * @see {@linkcode ApiResolver.loadedGlobalRef}.
   */
  get [unsafePropertyName]() {
    return this.loadedGlobalRef;
  }

  protected override readonly globalPropertyName = globalPropertyName;

  protected override initialize(
    window: Window.AsUninitialized
  ): asserts window is Window.AsInitialized {
    // Inspired by https://developers.google.com/custom-search-ads/docs/implementation-guide
    if (window[this.globalPropertyName]) return;

    // eslint-disable-next-line func-names
    const globalRef = (window[this.globalPropertyName] = function () {
      // eslint-disable-next-line prefer-rest-params
      (globalRef.q ??= []).push(arguments);
    } as NonNullable<Api.Uninitialized>);

    globalRef.t = +new Date();
  }

  protected override isEnabled(serverInitialState?: InitialState) {
    const config = serverInitialState?.config ?? $config.get();
    return !!config?.adServer.afs;
  }

  protected override isInitialized(
    window: typeof globalThis.window
  ): window is Window.AsInitialized {
    return (
      typeof window[this.globalPropertyName] === 'function' &&
      window[this.globalPropertyName]?.t !== undefined
    );
  }

  protected override isLoaded(
    globalRef: Api.Initialized | Api.Loaded
  ): globalRef is Api.Loaded {
    return (
      Boolean(globalRef) &&
      'google' in window &&
      typeof window.google === 'object' &&
      !!window.google &&
      'ads' in window.google &&
      typeof window.google.ads === 'object' &&
      !!window.google.ads &&
      'search' in window.google.ads &&
      typeof window.google.ads.search === 'object' &&
      !!window.google.ads.search
    );
  }

  protected override enqueue(
    _globalRef: Api.Initialized | Api.Loaded,
    executor: () => void
  ) {
    executor();
  }
}
